import React from 'react';
import { ShieldCheck, CheckCircle2, Mail, MapPin, MessageSquare, ExternalLink, ArrowRight } from 'lucide-react';
import { useApp, AppView } from '../../context/AppContext';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { JobMatchLogo } from '../common/JobMatchLogo';
import { TELEGRAM_CHAT_URL, TELEGRAM_BOT_USERNAME } from '../../config/telegram';
import { TelegramIcon } from '../TelegramAssistantSection';
import { InstallAppButton } from '../pwa/PWAInstallManager';

export const Footer: React.FC = () => {
  const { navigateTo, openAuthModal } = useApp();

  const handleLink = (view: AppView, params?: Record<string, any>) => {
    navigateTo(view, params);
  };

  return (
    <footer className="bg-slate-900 text-slate-400 text-xs sm:text-sm border-t border-slate-800 mt-auto">
      {/* Trust & Verification Guarantee Banner */}
      <div className="bg-slate-950 border-b border-slate-800 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-800 flex items-center justify-center text-emerald-400 shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white font-bold text-xs sm:text-sm">4-Step Identity Vetting</h4>
              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                NIN validation, operational address document, LGA service zone verification, and real-time facial liveness check.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-800 flex items-center justify-center text-emerald-400 shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white font-bold text-xs sm:text-sm">Transparent Itemized Quotes</h4>
              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                Compare genuine quotes with clear labor and materials breakdown in Naira before selecting your professional.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-800 flex items-center justify-center text-emerald-400 shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white font-bold text-xs sm:text-sm">Coverage Across Nigeria</h4>
              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                Active in Lagos, Abuja FCT, Port Harcourt, Ibadan, and key commercial hubs spanning all 36 Nigerian States.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main 5-Column Nigerian Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
          {/* Column 1: Brand Info & Mission */}
          <div className="sm:col-span-2 lg:col-span-1 space-y-4">
            <div className="flex items-center gap-2 bg-white/95 p-2 rounded-2xl w-fit">
              <JobMatchLogo variant="compact" size="md" />
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              JobMatch NG connects customers with verified skilled professionals and technicians across Nigeria for reliable home and commercial repairs.
            </p>
            <div className="space-y-2 text-xs text-slate-400 pt-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Lagos, Nigeria</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <a href="mailto:support@jobmatch.ng" className="hover:text-emerald-400 transition-colors">
                  support@jobmatch.ng
                </a>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Multi-step identity and trade vetting</span>
              </div>
            </div>

            <div className="pt-2 space-y-2">
              <a
                href={TELEGRAM_CHAT_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full py-2.5 px-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm"
              >
                <TelegramIcon className="w-4 h-4" />
                <span>Telegram AI: @{TELEGRAM_BOT_USERNAME}</span>
              </a>
              <InstallAppButton variant="footer" />
            </div>
          </div>

          {/* Column 2: For Customers */}
          <div className="space-y-3">
            <h5 className="text-white font-extrabold text-xs uppercase tracking-wider">
              For Customers
            </h5>
            <ul className="space-y-2 text-xs">
              <li>
                <a
                  href="/post-job"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('post_job');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Post a Job Free
                </a>
              </li>
              <li>
                <a
                  href="/find-a-professional"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('find_pro');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Find a Professional
                </a>
              </li>
              <li>
                <a
                  href="/how-it-works"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('how_it_works');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  How It Works
                </a>
              </li>
              <li>
                <a
                  href="/for-customers"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('customer_guide');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Customer Service Guide
                </a>
              </li>
              <li>
                <a
                  href="/verification"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('verification');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Trust &amp; Verification
                </a>
              </li>
              <li>
                <a
                  href="/faq"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('faq');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Customer FAQ
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: For Professionals / Artisans */}
          <div className="space-y-3">
            <h5 className="text-white font-extrabold text-xs uppercase tracking-wider">
              For Professionals
            </h5>
            <ul className="space-y-2 text-xs">
              <li>
                <a
                  href="/become-a-professional"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('become_pro');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Become a Professional
                </a>
              </li>
              <li>
                <a
                  href="/verification"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('verification');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Verification Requirements
                </a>
              </li>
              <li>
                <button
                  onClick={() => handleLink('available_jobs')}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Browse Available Jobs
                </button>
              </li>
              <li>
                <a
                  href="/faq"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('faq');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Professional FAQ
                </a>
              </li>
              <li>
                <button
                  onClick={() => openAuthModal('login')}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Sign In to Account
                </button>
              </li>
              <li>
                <button
                  onClick={() => openAuthModal('register')}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Create an Account
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: Service Categories */}
          <div className="space-y-3">
            <h5 className="text-white font-extrabold text-xs uppercase tracking-wider">
              Service Categories
            </h5>
            <ul className="space-y-2 text-xs">
              {['plumber', 'electrician', 'carpenter', 'painter', 'ac-technician', 'generator-technician'].map((slug) => {
                const cat = SERVICE_CATEGORIES.find((c) => c.slug === slug);
                if (!cat) return null;
                return (
                  <li key={slug}>
                    <a
                      href={`/services/${slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink('find_pro', { selectedCategory: slug });
                      }}
                      className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                );
              })}
              <li>
                <a
                  href="/find-a-professional"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('find_pro');
                  }}
                  className="text-emerald-400 hover:text-emerald-300 font-bold transition-colors inline-flex items-center gap-1 cursor-pointer pt-1"
                >
                  <span>All Categories</span>
                  <ArrowRight className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 5: Company & Legal */}
          <div className="space-y-3">
            <h5 className="text-white font-extrabold text-xs uppercase tracking-wider">
              Company &amp; Legal
            </h5>
            <ul className="space-y-2 text-xs">
              <li>
                <a
                  href="/about"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('about');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  About JobMatch NG
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('contact');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a
                  href="/privacy"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('privacy');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Privacy Policy (NDPA 2023)
                </a>
              </li>
              <li>
                <a
                  href="/terms"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('terms');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Terms &amp; Conditions
                </a>
              </li>
              <li>
                <a
                  href="/verification"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLink('verification');
                  }}
                  className="hover:text-emerald-400 transition-colors text-left cursor-pointer"
                >
                  Trust &amp; Safety
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500 gap-4 text-center md:text-left">
          <div className="space-y-1">
            <p>© {new Date().getFullYear()} JobMatch NG. All rights reserved.</p>
            <p className="text-[11px] text-slate-500">
              Connecting verified Nigerian professionals with quality jobs. All prices in Nigerian Naira (₦).
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-[11px]">
            <a
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                handleLink('privacy');
              }}
              className="hover:text-slate-300 transition-colors cursor-pointer"
            >
              Privacy Policy
            </a>
            <span>•</span>
            <a
              href="/terms"
              onClick={(e) => {
                e.preventDefault();
                handleLink('terms');
              }}
              className="hover:text-slate-300 transition-colors cursor-pointer"
            >
              Terms of Service
            </a>
            <span>•</span>
            <a
              href="/verification"
              onClick={(e) => {
                e.preventDefault();
                handleLink('verification');
              }}
              className="hover:text-slate-300 transition-colors cursor-pointer"
            >
              Verification Policy
            </a>
            <span>•</span>
            <span className="text-emerald-500 font-medium">Built for Nigeria 🇳🇬</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
